// PremiseTab.tsx
import { usePremise } from "@features/mx/routeManagement/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Card } from "@shared/ui/card";
import { Checkbox } from "@shared/ui/checkbox";
import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@shared/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import {
  useGetArea,
  useGetSubArea,
} from "@features/mx/readCycleManagement/hooks";
import { MODAL_PAGE_SIZE } from "@shared/utils/constants";
interface PremiseTabProps {
  selectedPremises: string[];
  setSelectedPremises: React.Dispatch<React.SetStateAction<string[]>>;
}

export const PremiseTable = ({
  selectedPremises,
  setSelectedPremises,
}: PremiseTabProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = Number(searchParams.get("page") || "1");
  const currentSearch = decodeURIComponent(
    searchParams.get("search_data") || "",
  );
  const currentArea = searchParams.get("area") || "all";
  const currentSubArea = searchParams.get("sub_area") || "all";

  const [pendingFilters, setPendingFilters] = useState({
    area: currentArea,
    subArea: currentSubArea,
  });

  const { data: areaData } = useGetArea();
  const { data: subAreaData } = useGetSubArea(
    currentArea !== "all" ? [currentArea] : [],
  );

  const { data: premiseData } = usePremise(
    currentPage,
    MODAL_PAGE_SIZE,
    currentArea,
    currentSubArea,
    currentSearch,
  );

  const premiseDetail =
    premiseData?.result?.map((item: any) => ({
      code: item.code || item.hierarchyCodes?.premise,
      name: item.name || "NA",
      area: item.hierarchyNames?.area || "NA",
      subArea: item.hierarchyNames?.subArea || "NA",
      meters: item.installedMeterCount ?? "NA",
      consumers: item.billedUnitCount ?? "NA",
    })) || [];

  const areaOptions = useMemo(
    () =>
      areaData?.map((area) => ({
        value: String(area.code),
        label: area.name,
      })) || [],
    [areaData],
  );

  const subAreaOptions = useMemo(
    () =>
      subAreaData?.map((subArea) => ({
        value: String(subArea.code),
        label: subArea.name,
      })) || [],
    [subAreaData],
  );

  const handlePendingFilterChange = (
    key: keyof typeof pendingFilters,
    value: string,
  ) => {
    setPendingFilters((prev) => {
      if (key === "area") {
        return {
          ...prev,
          [key]: value,
          subArea: "all",
        };
      }
      return {
        ...prev,
        [key]: value,
      };
    });
  };

  const handleApplyFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      area: pendingFilters.area,
      sub_area: pendingFilters.subArea,
      page: 1,
    };
    setSearchParams(newParams);
  };

  const handleResetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };
    delete newParams.area;
    delete newParams.sub_area;
    delete newParams.search_data;
    newParams.page = 1;
    setSearchParams(newParams);
    setPendingFilters({
      area: "all",
      subArea: "all",
    });
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      area: currentArea,
      sub_area: currentSubArea,
    };
    if (searchTerm.trim()) newParams.search_data = searchTerm.trim();
    else delete newParams.search_data;
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
  };

  const filterPanel = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Area</label>
          <Select
            value={pendingFilters.area}
            onValueChange={(value) => handlePendingFilterChange("area", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select area" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Areas</SelectItem>
              {areaOptions.map((area) => (
                <SelectItem key={area.value} value={area.value}>
                  {area.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label
            className={`block text-sm font-medium mb-1 ${
              pendingFilters.area === "all" ? "text-gray-400" : "text-gray-700"
            }`}
          >
            Sub Area
          </label>
          <Select
            value={pendingFilters.subArea}
            onValueChange={(value) =>
              handlePendingFilterChange("subArea", value)
            }
            disabled={pendingFilters.area === "all"}
          >
            <SelectTrigger
              className={`w-full ${
                pendingFilters.area === "all"
                  ? "bg-gray-100 border-gray-300 cursor-not-allowed text-gray-400 opacity-60"
                  : "bg-white"
              }`}
            >
              <SelectValue placeholder="Select sub area" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sub Areas</SelectItem>
              {subAreaOptions.map((subArea) => (
                <SelectItem key={subArea.value} value={subArea.value}>
                  {subArea.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="default" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
        <Button variant="ghost" onClick={handleResetFilters}>
          Clear
        </Button>
      </div>
    </div>
  );

  const columns: TableColumn<any>[] = [
    {
      key: "name",
      header: "Premise",
      headerTooltip:
        "Building or location name. Each premise can contain multiple meters and consumers.",
    },
    {
      key: "area",
      header: "Area",
      headerTooltip:
        "Geographic zone for route planning. Use to group nearby premises and optimize travel time.",
    },
    {
      key: "subArea",
      header: "Subarea",
      headerTooltip:
        "Subdivision within an area for granular route organization and workload distribution.",
    },
    {
      key: "meters",
      header: "Resources",
      headerTooltip:
        "Number of meters at this premise. Higher counts indicate larger workload for field readers.",
    },
  ];

  return (
    <Card className="shadow-sm">
      {selectedPremises.length > 0 && (
        <div className="flex items-center justify-between mb-3 bg-blue-50 border border-blue-100 text-blue-700 text-sm px-3 py-2 rounded-md shadow-sm">
          <span className="font-medium">
            {selectedPremises.length} premise
            {selectedPremises.length > 1 ? "s" : ""} selected
          </span>
        </div>
      )}

      <GenericTable
        data={premiseDetail}
        columns={columns}
        rowKey="code"
        selectable={true}
        selectedRows={selectedPremises}
        onSelectionChange={setSelectedPremises}
        pagination={{
          currentPage,
          totalPages: Math.ceil(
            (premiseData?.count || premiseDetail.length) / 10,
          ),
          totalItems: premiseData?.count || premiseDetail.length,
          itemsPerPage: 10,
          startItem: (currentPage - 1) * 10 + 1,
          endItem: Math.min(
            currentPage * 10,
            premiseData?.count || premiseDetail.length,
          ),
        }}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search premises..."
        emptyMessage="No premises found"
        filterPanel={filterPanel}
      />
    </Card>
  );
};
